import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { CoachAvatars } from "./_shared";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

function timeAgo(iso, t) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("circle_news_just_now");
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

export default function CollabChats() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("client_collab_chats_title"));
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/client/collab/chats"); setChats(data?.chats || []); }
    catch { setChats([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openChat = (c) => navigate(c.kind === "shared_chat" ? `/app/shared-chat/${c.id}` : `/app/messages/${c.id}`);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="client-collab-chats">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/collab")} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("client_collab_chats_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("client_collab_chats_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : chats.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("client_collab_chats_empty")}</p>
      ) : (
        <div className="space-y-2.5">
          {chats.map((c) => (
            <button key={c.id} data-testid={`client-collab-chat-${c.id}`} onClick={() => openChat(c)}
              className="flex w-full items-center gap-3 rounded-2xl border border-border bg-secondary/60 p-3.5 text-left transition-colors hover:bg-accent">
              <CoachAvatars coaches={c.coaches} size={34} max={2} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-display text-sm text-foreground">{c.coaches.map((co) => co.name).join(" + ")}</span>
                  {c.kind === "shared_chat" ? <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-primary">{t("client_collab_kind_shared")}</span> : null}
                </div>
                <p className="truncate text-xs text-muted-foreground">{c.last_message || t("client_collab_no_messages")}</p>
                {c.last_message_at ? <p className="text-[10px] text-muted-foreground/70">{timeAgo(c.last_message_at, t)}</p> : null}
              </div>
              {c.unread_count > 0 ? <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-semibold text-white">{c.unread_count > 99 ? "99+" : c.unread_count}</span> : null}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
